import React, { useEffect, useState } from 'react'
import {
	Card,
	Typography,
	Row,
	Col,
	Spin,
	Avatar,
	Tag,
	Divider,
	Space,
	Button,
	Descriptions,
	Table,
} from 'antd'
import type { ColumnsType } from 'antd/es/table'
import { PhoneOutlined, MailOutlined, BankOutlined, IdcardOutlined } from '@ant-design/icons'
import GlobalHeader from '../../common/components/Header/GlobalHeader'
import { IStaff } from '../../Interfaces/staff.interface'
import { useParams, useNavigate } from 'react-router-dom'
import { staffService } from '../../common/service/staff.service'
import { leaveService } from '../../common/service'
import dayjs from 'dayjs'
import { useThemeColors } from '../../common/ThemeContext'

const { Title, Text } = Typography

interface ViewStaffDetailProps {
	staffId?: string
	embedded?: boolean
	onClose?: () => void
	onEdit?: (id: string) => void
}

function ViewStaffDetail({ staffId, embedded = false, onClose, onEdit }: ViewStaffDetailProps) {
	const { primary, gradient } = useThemeColors()
	const [loading, setLoading] = useState(false)
	const { id: routeId } = useParams()
	const id = staffId || routeId
	const [staff, setStaff] = useState<IStaff | null>(null)
	const navigate = useNavigate()

	// Leave balance
	const [balanceRows, setBalanceRows] = useState<
		Array<{ leaveType: string; entitlement: number; used: number; remaining: number }>
	>([])
	const [balanceLoading, setBalanceLoading] = useState(false)

	const toRankingLabel = (value?: string) => {
		if (value === 'manager_level_1') return 'Manager Level 1'
		if (value === 'manager_level_2') return 'Manager Level 2'
		if (value === 'manager_level_3') return 'Manager Level 3'
		if (value === 'executor') return 'Executor'
		return value || '-'
	}

	// Derived lists from multiline fields to render as bullets
	const experienceItems = (staff?.experience || '')
		.split(/\r?\n/)
		.map((s) => s.trim())
		.filter(Boolean)
	const educationItems = (staff?.education || '')
		.split(/\r?\n/)
		.map((s) => s.trim())
		.filter(Boolean)
	const experienceBlocks = staff?.experience
		? staff.experience.split(/\n\s*\n/).map((b) =>
				b
					.split(/\r?\n/)
					.map((s) => s.trim())
					.filter(Boolean),
			)
		: []
	const educationBlocks = staff?.education
		? staff.education.split(/\n\s*\n/).map((b) =>
				b
					.split(/\r?\n/)
					.map((s) => s.trim())
					.filter(Boolean),
			)
		: []

	const fetchLeaveBalance = async (staffId: string) => {
		setBalanceLoading(true)
		try {
			const res: any = await leaveService.getBalance(staffId)
			const data = res?.data ?? res
			if (data?.entitlements) {
				const rows = Object.keys(data.entitlements).map((type) => ({
					leaveType: type,
					entitlement: data.entitlements[type] ?? 0,
					used: (data.used ?? {})[type] ?? 0,
					remaining: (data.remaining ?? {})[type] ?? 0,
				}))
				setBalanceRows(rows)
			}
		} catch {
			// silently ignore — balance section simply stays empty
		} finally {
			setBalanceLoading(false)
		}
	}

	const fetchDataById = async () => {
		if (!id) return
		setLoading(true)
		try {
			const res: any = await staffService.detail(id)
			if (res?.statusCode === 200) {
				// Convert Firestore timestamp to ISO string if needed
				const staffData = { ...res.data }
				if (staffData.createdAt?._seconds) {
					staffData.createdAt = new Date(
						staffData.createdAt._seconds * 1000,
					).toISOString()
				}
				if (staffData.updatedAt?._seconds) {
					staffData.updatedAt = new Date(
						staffData.updatedAt._seconds * 1000,
					).toISOString()
				}
				console.log('Staff data loaded:', staffData)
				console.log('Photo URL:', staffData.photoUrl)
				setStaff(staffData)
			}
		} catch (error) {
			console.error('Error fetching staff details:', error)
		}
		setLoading(false)
	}

	useEffect(() => {
		if (id) {
			fetchDataById()
			fetchLeaveBalance(id)
		}
	}, [id])

	return (
		<>
			{!embedded && <GlobalHeader />}
			{!embedded && (
				<Card style={{ margin: '20px', textAlign: 'center' }}>
					<Title
						level={2}
						style={{ fontFamily: "'CoreSansCR-65Bold', sans-serif", color: primary }}
					>
						Staff Profile
					</Title>
				</Card>
			)}

			{loading ? (
				<Spin
					tip='Loading...'
					style={{ display: 'block', textAlign: 'center', marginTop: '20px' }}
				/>
			) : staff ? (
				<Card
					style={{
						margin: '20px',
						padding: 24,
						boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
						background: '#f8fafc',
					}}
				>
					{/* Header with photo, name, and actions */}
					<div
						style={{
							background: gradient,
							padding: '32px 24px',
							borderRadius: 12,
							marginBottom: 24,
							boxShadow: '0 4px 12px rgba(37, 74, 137, 0.3)',
							display: 'flex',
							alignItems: 'center',
							justifyContent: 'space-between',
						}}
					>
						<div style={{ display: 'flex', alignItems: 'center', gap: 24 }}>
							<div
								style={{
									position: 'relative',
									padding: 4,
									background: 'linear-gradient(135deg, #fff 0%, #f8fafc 100%)',
									borderRadius: '50%',
									boxShadow: '0 8px 24px rgba(0, 0, 0, 0.3)',
								}}
							>
								<Avatar
									size={120}
									src={staff?.photoUrl}
									style={{
										border: '5px solid #fff',
										boxShadow: '0 4px 16px rgba(0,0,0,0.2)',
										background: '#e2e8f0',
									}}
								/>
							</div>
							<div>
								<Title
									level={3}
									style={{
										margin: 0,
										color: '#fff',
										textShadow: '0 2px 4px rgba(0,0,0,0.2)',
									}}
								>
									{`${staff?.lastName || ''} ${staff?.firstName || ''}`}
								</Title>
								{staff?.nameInEnglish && (
									<Text
										style={{
											color: '#e2e8f0',
											fontSize: 16,
											display: 'block',
											marginTop: 4,
										}}
									>
										{staff.nameInEnglish}
									</Text>
								)}
								<div style={{ marginTop: 12 }}>
									{staff?.role?.name && (
										<Tag
											color='blue'
											style={{ marginRight: 8, fontWeight: 500 }}
										>
											{staff.role.name}
										</Tag>
									)}
									{staff?.department?.name && (
										<Tag color='geekblue' style={{ fontWeight: 500 }}>
											{staff.department.name}
										</Tag>
									)}
									{staff?.status && (
										<Tag
											color={staff.status === 'Active' ? 'green' : 'red'}
											style={{ marginLeft: 8, fontWeight: 500 }}
										>
											{staff.status}
										</Tag>
									)}
								</div>
							</div>
						</div>
						<div>
							<Button
								onClick={() => {
									if (embedded) {
										onClose?.()
										return
									}

									navigate('/staff/list')
								}}
								style={{ marginRight: 8 }}
								size='large'
							>
								Back
							</Button>
							<Button
								type='primary'
								onClick={() => {
									if (!id) {
										return
									}

									if (embedded) {
										onEdit?.(id)
										return
									}

									navigate(`/staff/${id}`)
								}}
								size='large'
							>
								Edit
							</Button>
						</div>
					</div>

					<Row gutter={[20, 20]}>
						<Col xs={24} md={12}>
							{/* Personal Information Card */}
							<Card
								title={
									<span style={{ color: primary, fontWeight: 600 }}>
										Personal Information
									</span>
								}
								size='small'
								style={{
									marginBottom: 20,
									boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
								}}
							>
								<Descriptions column={1} size='small' colon={false}>
									{staff?.id && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Staff ID
												</Text>
											}
										>
											{staff.id}
										</Descriptions.Item>
									)}
									{staff?.sex && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Gender
												</Text>
											}
										>
											{staff.sex === 'M'
												? 'Male'
												: staff.sex === 'F'
													? 'Female'
													: staff.sex}
										</Descriptions.Item>
									)}
									{staff?.department?.name && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Department
												</Text>
											}
										>
											{staff.department.name}
										</Descriptions.Item>
									)}
									{staff?.role?.name && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Role
												</Text>
											}
										>
											{staff.role.name}
										</Descriptions.Item>
									)}
									{staff?.shift && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Shift/Team
												</Text>
											}
										>
											{staff.shift.name || staff.shift.id}
										</Descriptions.Item>
									)}
									{staff?.ranking && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Ranking
												</Text>
											}
										>
											{toRankingLabel(staff.ranking)}
										</Descriptions.Item>
									)}
									{staff?.firstLineManager && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													First Line Manager
												</Text>
											}
										>
											{staff.firstLineManager.name ||
												staff.firstLineManager.id}
										</Descriptions.Item>
									)}
									{staff?.secondLineManager && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Second Line Manager
												</Text>
											}
										>
											{staff.secondLineManager.name ||
												staff.secondLineManager.id}
										</Descriptions.Item>
									)}
									{staff?.thirdLineManager && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Third Line Manager
												</Text>
											}
										>
											{staff.thirdLineManager.name || staff.thirdLineManager.id}
										</Descriptions.Item>
									)}
									{staff?.isExtraShift && staff?.extraShift && (
										<Descriptions.Item
											label={
												<Text strong style={{ color: '#64748b' }}>
													Extra Shift
												</Text>
											}
										>
											{staff.extraShift}
										</Descriptions.Item>
									)}
								</Descriptions>
							</Card>
						</Col>

						<Col xs={24} md={12}>
							{/* Contact Information Card */}
							<Card
								title={
									<span style={{ color: primary, fontWeight: 600 }}>
										Contact Information
									</span>
								}
								size='small'
								style={{
									marginBottom: 20,
									boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
								}}
							>
								<Descriptions column={1} size='small' colon={false}>
									{staff?.phoneNumber && (
										<Descriptions.Item
											label={
												<>
													<PhoneOutlined
														style={{ color: primary, marginRight: 6 }}
													/>
													<Text strong style={{ color: '#64748b' }}>
														Phone
													</Text>
												</>
											}
										>
											<a href={`tel:${staff.phoneNumber}`}>
												{staff.phoneNumber}
											</a>
										</Descriptions.Item>
									)}
									{staff?.email && (
										<Descriptions.Item
											label={
												<>
													<MailOutlined
														style={{ color: primary, marginRight: 6 }}
													/>
													<Text strong style={{ color: '#64748b' }}>
														Email
													</Text>
												</>
											}
										>
											<a href={`mailto:${staff.email}`}>{staff.email}</a>
										</Descriptions.Item>
									)}
									{staff?.bankAccountNumber && (
										<Descriptions.Item
											label={
												<>
													<BankOutlined
														style={{ color: primary, marginRight: 6 }}
													/>
													<Text strong style={{ color: '#64748b' }}>
														Bank Account
													</Text>
												</>
											}
										>
											{staff.bankAccountNumber}
										</Descriptions.Item>
									)}
								</Descriptions>
							</Card>

							{/* Bio Section */}
							{staff?.bio && (
								<Card
									title={
										<span style={{ color: primary, fontWeight: 600 }}>
											Biography
										</span>
									}
									size='small'
									style={{
										marginBottom: 20,
										boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
									}}
								>
									<Text style={{ whiteSpace: 'pre-wrap' }}>{staff.bio}</Text>
								</Card>
							)}

							{/* Experience Section */}
							{experienceBlocks.length > 0 && (
								<Card
									title={
										<span style={{ color: primary, fontWeight: 600 }}>
											Experience
										</span>
									}
									size='small'
									style={{
										marginBottom: 20,
										boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
									}}
								>
									{experienceBlocks.map((block, idx) => (
										<div
											key={idx}
											style={{
												marginBottom:
													idx < experienceBlocks.length - 1 ? 16 : 0,
											}}
										>
											{block.map((line, lineIdx) => (
												<div
													key={lineIdx}
													style={{
														marginBottom: 4,
														color: lineIdx === 0 ? primary : '#64748b',
														fontWeight: lineIdx === 0 ? 600 : 400,
													}}
												>
													{line}
												</div>
											))}
										</div>
									))}
								</Card>
							)}

							{/* Education Section */}
							{educationBlocks.length > 0 && (
								<Card
									title={
										<span style={{ color: primary, fontWeight: 600 }}>
											Education
										</span>
									}
									size='small'
									style={{
										marginBottom: 20,
										boxShadow: '0 1px 4px rgba(0,0,0,0.06)',
									}}
								>
									{educationBlocks.map((block, idx) => (
										<div
											key={idx}
											style={{
												marginBottom:
													idx < educationBlocks.length - 1 ? 16 : 0,
											}}
										>
											{block.map((line, lineIdx) => (
												<div
													key={lineIdx}
													style={{
														marginBottom: 4,
														color: lineIdx === 0 ? primary : '#64748b',
														fontWeight: lineIdx === 0 ? 600 : 400,
													}}
												>
													{line}
												</div>
											))}
										</div>
									))}
								</Card>
							)}

							{/* Metadata */}
							<Card
								size='small'
								style={{ background: '#f1f5f9', borderColor: '#e2e8f0' }}
							>
								<Row gutter={[16, 16]}>
									<Col xs={24} sm={12}>
										<Text type='secondary' style={{ fontSize: 12 }}>
											Created
										</Text>
										<div style={{ color: '#475569', fontSize: 14 }}>
											{staff?.createdAt
												? dayjs(staff.createdAt).format('MMM D, YYYY HH:mm')
												: '-'}
										</div>
									</Col>
									<Col xs={24} sm={12}>
										<Text type='secondary' style={{ fontSize: 12 }}>
											Updated
										</Text>
										<div style={{ color: '#475569', fontSize: 14 }}>
											{staff?.updatedAt
												? dayjs(staff.updatedAt).format('MMM D, YYYY HH:mm')
												: '-'}
										</div>
									</Col>
								</Row>
							</Card>
						</Col>
					</Row>

					{/* ── Leave Balance ───────────────────────────────────── */}
					<Card
						title={
							<span style={{ color: primary, fontWeight: 600 }}>
								Leave Balance ({new Date().getFullYear()})
							</span>
						}
						size='small'
						style={{ marginTop: 20, boxShadow: '0 1px 4px rgba(0,0,0,0.06)' }}
					>
						<Table<{
							leaveType: string
							entitlement: number
							used: number
							remaining: number
						}>
							loading={balanceLoading}
							dataSource={balanceRows}
							rowKey='leaveType'
							pagination={false}
							size='small'
							columns={
								[
									{
										title: 'Leave Type',
										dataIndex: 'leaveType',
										key: 'leaveType',
										render: (v: string) => <Text strong>{v}</Text>,
									},
									{
										title: 'Entitlement (days)',
										dataIndex: 'entitlement',
										key: 'entitlement',
										align: 'center' as const,
									},
									{
										title: 'Used (days)',
										dataIndex: 'used',
										key: 'used',
										align: 'center' as const,
									},
									{
										title: 'Remaining (days)',
										dataIndex: 'remaining',
										key: 'remaining',
										align: 'center' as const,
										render: (v: number) => (
											<Tag
												color={
													v > 0 ? 'green' : v === 0 ? 'default' : 'red'
												}
											>
												{v}
											</Tag>
										),
									},
								] as ColumnsType<{
									leaveType: string
									entitlement: number
									used: number
									remaining: number
								}>
							}
						/>
					</Card>
				</Card>
			) : null}
		</>
	)
}

export default ViewStaffDetail
